<template>
  <div>
    <div class="d-flex align-center justify-space-between ga-3 mb-4">
      <div>
        <div class="font-weight-bold font-size-15">
          Конструктор полів
          <span class="text-disabled">({{ fields.length }})</span>
        </div>
        <div class="text-disabled">
          Перетягніть поля для зміни порядку · Клік для редагування
        </div>
      </div>

      <v-btn @click="fieldAdd">
        <v-icon class="mr-2" icon="mdi-plus" />
        Додати
      </v-btn>
    </div>

    <b-global-loader :loading="$loading.isLoading('docTypeFieldList')">
      <div v-if="!fields.length" class="text-center text-disabled py-4">
        Полів не додано. Натисніть «Додати» для початку.
      </div>
      <draggable
        v-else
        v-model="fields"
        :animation="400"
        ghostClass="ghostClass"
        chosenClass="chosenClass"
        dragClass="dragClass"
        @change="
          fieldUpdateSortOrder($event.moved.element.id, $event.moved.newIndex)
        ">
        <v-card
          v-for="field in fields"
          :key="field.id"
          class="d-flex w-100 mb-2"
          border>
          <div
            style="border-right: 1px solid #f0f0f5; background: #fafafa"
            class="d-flex flex-column align-center py-2 px-3">
            <v-icon
              v-tooltip="'Перетягніть для зміни порядку'"
              class="cursor-grab"
              icon="mdi-dots-grid"
              size="small"
              color="grey"></v-icon>
            <v-icon
              style="color: #9898a8"
              icon="mdi-chevron-up"
              :disabled="isFirstField(field)"
              @click="moveFieldUp(field)"></v-icon>
            <span style="color: #9898a8" class="font-weight-bold">{{
              field.sortOrder + 1
            }}</span>
            <v-icon
              style="color: #9898a8"
              icon="mdi-chevron-down"
              :disabled="isLastField(field)"
              @click="moveFieldDown(field)"></v-icon>
          </div>
          <div style="border-right: 1px solid #f0f0f5" class="w-100 pa-3">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold font-size-15">
                {{ field.fieldDefinition.name }}
              </span>
              <div class="d-flex ga-2">
                <v-chip
                  style="font-size: 10px !important"
                  class="font-weight-bold text-uppercase"
                  density="compact"
                  rounded="lg"
                  :color="getFieldTypeColor(field.fieldDefinition.fieldType)">
                  {{
                    $directory
                      .get('fieldTypes', null, { type: 'collection' })
                      .getItem(field.fieldDefinition.fieldType)?.name ||
                    field.fieldDefinition.fieldType
                  }}
                </v-chip>
                <v-chip
                  v-if="field.entitySource"
                  style="font-size: 10px !important"
                  class="font-weight-bold"
                  variant="text"
                  border
                  density="compact"
                  rounded="lg">
                  <v-icon
                    icon="mdi-link-variant"
                    class="mr-2"
                    color="purple"></v-icon>
                  <span>
                    {{ getEntitySourceLabel(field.entitySource) }}
                  </span>
                </v-chip>
              </div>
            </div>
            <div class="d-flex align-center ga-3">
              <v-chip
                v-if="field.isRequired"
                density="compact"
                color="blue"
                rounded="pill"
                size="small">
                <v-icon icon="mdi-arrow-down" class="mr-2"></v-icon>
                <span class="font-weight-bold">Обовʼязкове</span>
              </v-chip>

              <v-chip
                v-if="!field.isHidden"
                density="compact"
                color="success"
                rounded="pill"
                size="small">
                <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                <span class="font-weight-bold">В картці</span>
              </v-chip>

              <v-chip
                v-if="field.isReadonly"
                density="compact"
                color="warning"
                rounded="pill"
                size="small">
                <v-icon icon="mdi-pencil-remove-outline" class="mr-2"></v-icon>
                <span class="font-weight-bold">Тільки перегляд</span>
              </v-chip>
            </div>
          </div>
          <div>
            <div
              class="d-flex justify-center"
              style="border-bottom: 1px solid #f0f0f5">
              <v-btn
                icon="intime:pencil"
                variant="text"
                color="blue"
                density="comfortable"
                height="3rem"
                size="x-small"
                @click="fieldUpdate(field)"></v-btn>
            </div>
            <div>
              <v-btn
                icon="intime:delete"
                variant="text"
                color="error"
                density="comfortable"
                height="3rem"
                size="small"
                @click="fieldDelete(field.id)"></v-btn>
            </div>
          </div>
        </v-card>
      </draggable>

      <div v-if="fields.length" class="mt-8">
        <v-divider class="mb-5"></v-divider>

        <div class="mb-5">
          <div class="font-weight-bold font-size-18">
            Попередній перегляд картки
          </div>
          <div class="text-disabled">
            Перетягніть поля для зміни порядку · Натисніть кнопку
            <span class="font-weight-bold text-light-blue-darken-4">1/3</span>
            у куті поля щоб змінити ширину
          </div>
        </div>

        <div class="border rounded-lg pa-6">
          <v-row align="center">
            <draggable
              v-model="fields"
              :animation="400"
              style="display: contents"
              ghostClass="ghostClass"
              chosenClass="chosenClass"
              dragClass="dragClass"
              @change="
                fieldUpdateSortOrder(
                  $event.moved.element.id,
                  $event.moved.newIndex
                )
              ">
              <v-col
                v-for="field in fields"
                :key="field.id"
                cols="12"
                :md="getRenderSizeCols(field.renderSize)">
                <v-hover v-slot="{ isHovering, props: hoverProps }">
                  <div v-bind="hoverProps">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="d-flex ga-1">
                        <b-label
                          :label="field.label || field.fieldDefinition.name" />
                        <div v-if="field.isRequired" class="text-error">*</div>
                      </div>
                      <div class="d-flex align-center ga-2">
                        <v-icon
                          v-show="isHovering"
                          class="cursor-grab"
                          color="grey"
                          icon="mdi-dots-grid"
                          density="compact"
                          size="small" />
                        <div
                          v-show="isHovering"
                          v-ripple
                          class="font-size-12 text-light-blue-darken-4 font-weight-bold px-1 cursor-pointer"
                          style="border: 1px solid #f0f0f5; border-radius: 4px"
                          @click.stop="changeRenderSize(field)">
                          {{ getRenderSizeLabel(field.renderSize) }}
                        </div>
                      </div>
                    </div>
                    <b-input model-value="---" readonly hide-details />
                  </div>
                </v-hover>
              </v-col>
            </draggable>
          </v-row>
        </div>
      </div>
    </b-global-loader>
  </div>
</template>

<script lang="ts" setup>
import { VueDraggableNext as Draggable } from 'vue-draggable-next'
import { BGlobalLoader, BInput, BLabel } from 'best-modules/components'
import { useDocType } from '../docType'
import { fillDirectory, getDirectory } from '@/directory'
import { getFieldTypeColor } from '@/pages/administration/doc-types/helpers'
import { DocumentTypeField } from '@/utils/types/documentTypeField'

defineOptions({
  name: 'Fields',
})

fillDirectory('fieldTypes')
fillDirectory('docTypeFieldEntitySources')

type RenderSize = DocumentTypeField['renderSize']

const {
  fields,
  fieldAdd,
  fieldUpdate,
  fieldDelete,
  fieldChangeRenderSize,
  fieldUpdateSortOrder,
} = useDocType()

const renderSizeOptions: Record<
  RenderSize,
  { label: string; cols: number; next: RenderSize }
> = {
  small: { label: '1/3', cols: 4, next: 'medium' },
  medium: { label: '2/3', cols: 8, next: 'large' },
  large: { label: '1/1', cols: 12, next: 'small' },
}

const getRenderSize = (renderSize?: RenderSize): RenderSize => {
  return renderSize && renderSizeOptions[renderSize] ? renderSize : 'small'
}

const getRenderSizeLabel = (renderSize?: RenderSize): string => {
  return renderSizeOptions[getRenderSize(renderSize)].label
}

const getRenderSizeCols = (renderSize?: RenderSize): number => {
  return renderSizeOptions[getRenderSize(renderSize)].cols
}

const changeRenderSize = (field: DocumentTypeField) => {
  return fieldChangeRenderSize(
    field,
    renderSizeOptions[getRenderSize(field.renderSize)].next
  )
}

const sortedFields = () => {
  return [...fields.value].sort((a, b) => a.sortOrder - b.sortOrder)
}

const getFieldIndex = (field: DocumentTypeField): number => {
  return sortedFields().findIndex(f => f.id === field.id)
}

const isFirstField = (field: DocumentTypeField): boolean => {
  return getFieldIndex(field) <= 0
}

const isLastField = (field: DocumentTypeField): boolean => {
  const index = getFieldIndex(field)
  return index === -1 || index >= fields.value.length - 1
}

const moveFieldUp = (field: DocumentTypeField) => {
  const index = getFieldIndex(field)
  if (index <= 0) return

  const targetField = sortedFields()[index - 1]
  return fieldUpdateSortOrder(field.id, targetField.sortOrder)
}

const moveFieldDown = (field: DocumentTypeField) => {
  const index = getFieldIndex(field)
  const orderedFields = sortedFields()
  if (index === -1 || index >= orderedFields.length - 1) return

  const targetField = orderedFields[index + 1]
  return fieldUpdateSortOrder(field.id, targetField.sortOrder)
}

const getEntitySourceLabel = (key: string): string => {
  const item = getDirectory('docTypeFieldEntitySources').find(
    (s: any) => s.id === key
  )
  return item?.name ?? key
}
</script>
